/**
 * O PROBLEMA DO QUADRILÁTERO NO PLANO — comum aos três formatos de comando da habilidade
 * H3.11 (marcar na malha, escolher entre alternativas, preencher as coordenadas).
 *
 * Dois vértices OPOSTOS, A e C, são dados; B e D são pedidos. Dois níveis:
 *
 *  - 'retangulo': um retângulo de lados paralelos aos eixos. Cada vértice novo leva a
 *    abscissa de um ponto dado e a ordenada do outro — é ler coordenadas.
 *  - 'inclinado': um quadrado cujos lados NÃO são paralelos aos eixos, com os vértices
 *    ainda nos cruzamentos da malha. A diagonal nunca faz 45° com os eixos, e por isso
 *    "trocar as coordenadas" — a solução do nível anterior — nunca acerta por acaso. É
 *    preciso usar o ponto médio e girar a meia diagonal.
 *
 * Os ERROS TÍPICOS são calculados aqui, uma vez: o mesmo erro vira alternativa na
 * múltipla escolha, diagnóstico na ficha de coordenadas e nome na marcação da malha,
 * sempre com a mesma conta explicada com os números da variante.
 */

import type { Rng } from '../../core/rng.ts';
import type { Vec2 } from '../../geom/vec.ts';
import type { Rect, Scene, Shape } from '../../render/scene.ts';
import { scene } from '../../render/scene.ts';
import type { CoordinateEntryInteraction } from '../../schema/item.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  COORD_MISTURADAS,
  COORD_TROCADAS,
  QUAD_DIAGONAL_COMO_LADO,
  QUAD_GIRO_ERRADO,
  QUAD_LOSANGO,
  QUAD_RETANGULO,
  RET_FEZ_QUADRADO,
} from '../../schema/errors.ts';
import { numeroDe, verbo } from '../../core/pt.ts';
import { br, txt, type RichText } from '../../schema/richtext.ts';
import { eixosNumerados, gradeQuadriculada, mesmoConjunto, passoTxt, pontoTxt } from './plano.ts';

export type NivelQuadrilatero = 'retangulo' | 'inclinado';

export interface Quadrilatero {
  readonly nivel: NivelQuadrilatero;
  /** Vértices opostos, dados. */
  readonly A: Vec2;
  readonly C: Vec2;
  /** Vértices pedidos: ABCD percorre o contorno. */
  readonly B: Vec2;
  readonly D: Vec2;
}

/** As coordenadas dos pontos DADOS e PEDIDOS ficam entre −LIMITE e LIMITE. */
export const LIMITE = 5;

const cabe = (p: Vec2): boolean => Math.abs(p.x) <= LIMITE && Math.abs(p.y) <= LIMITE;

/**
 * Metade da diagonal do quadrado inclinado: com ela inteira, centro e vértices caem na
 * malha; com as duas coordenadas diferentes em módulo, a diagonal não faz 45°.
 */
const METADES: readonly (readonly [number, number])[] = [
  [1, 2],
  [1, 3],
  [2, 1],
  [2, 3],
  [3, 1],
  [3, 2],
];

export function sortearQuadrilatero(g: Rng, nivel: NivelQuadrilatero): Quadrilatero | null {
  const A = { x: g.int(-LIMITE, LIMITE), y: g.int(-LIMITE, LIMITE) };
  if (nivel === 'retangulo') {
    // Lados pares: o ponto médio cai na malha, e os erros de giro (que passam por ele)
    // também — senão metade dos distratores não existiria.
    const passos = [-6, -4, -2, 2, 4, 6];
    const dx = g.pick(passos);
    const dy = g.pick(passos);
    if (Math.abs(dx) === Math.abs(dy)) return null;
    const C = { x: A.x + dx, y: A.y + dy };
    const B = { x: C.x, y: A.y };
    const D = { x: A.x, y: C.y };
    return [C, B, D].every(cabe) ? { nivel, A, C, B, D } : null;
  }
  const [mx, my] = g.pick(METADES);
  const hx = g.bool() ? mx : -mx;
  const hy = g.bool() ? my : -my;
  const C = { x: A.x + 2 * hx, y: A.y + 2 * hy };
  const M = { x: A.x + hx, y: A.y + hy };
  const B = { x: M.x - hy, y: M.y + hx };
  const D = { x: M.x + hy, y: M.y - hx };
  return [C, B, D].every(cabe) ? { nivel, A, C, B, D } : null;
}

export const pontoMedio = (q: Quadrilatero): Vec2 => ({
  x: (q.A.x + q.C.x) / 2,
  y: (q.A.y + q.C.y) / 2,
});

/** Os dois pares aceitos: B e D podem trocar de lugar sem deixar de formar ABCD. */
export const respostasAceitas = (q: Quadrilatero): readonly (readonly [Vec2, Vec2])[] => [
  [q.B, q.D],
  [q.D, q.B],
];

export const acertou = (q: Quadrilatero, ms: readonly Vec2[]): boolean =>
  mesmoConjunto(ms, [q.B, q.D]);

export interface ErroQuadrilatero {
  readonly error: ErrorCode;
  readonly pontos: readonly [Vec2, Vec2];
  readonly explicacao: RichText;
}

const par = (b: Vec2, d: Vec2): string => `B = ${pontoTxt(b)} e D = ${pontoTxt(d)}`;

/**
 * Os erros típicos DESTA variante, cada um com os pontos que produz e a conta escrita.
 * Um erro cujos pontos coincidem com a resposta certa não entra: ele não discrimina.
 */
export function errosDoQuadrilatero(q: Quadrilatero): ErroQuadrilatero[] {
  const { A, C, B, D } = q;
  const M = pontoMedio(q);
  const v = { x: C.x - A.x, y: C.y - A.y };
  const h = { x: v.x / 2, y: v.y / 2 };
  const certo = par(B, D);
  const figura = q.nivel === 'retangulo' ? 'retângulo' : 'quadrado';
  const out: ErroQuadrilatero[] = [];
  const add = (error: ErrorCode, b: Vec2, d: Vec2, explicacao: RichText): void => {
    if (![b, d].every((p) => Number.isInteger(p.x) && Number.isInteger(p.y))) return;
    if (mesmoConjunto([b, d], [B, D])) return;
    if (out.some((e) => mesmoConjunto(e.pontos, [b, d]))) return;
    out.push({ error, pontos: [b, d], explicacao });
  };

  // Coordenadas invertidas: os pontos certos, escritos como (y, x).
  {
    const b = { x: B.y, y: B.x };
    const d = { x: D.y, y: D.x };
    // UM VÉRTICE SOBRE A DIAGONAL y = x NÃO SE MOVE AO INVERTER, e aí a frase dizia
    // que B = (1, 1) é o que o aluno errou e, três palavras depois, que B = (1, 1) é o
    // certo. Acontecia em 29% das variantes: basta a abscissa ser igual à ordenada num
    // dos dois vértices. Dizer POR QUE o ponto não mudou desfaz a contradição e ainda
    // ensina o que a inversão faz.
    const parados = [...(B.x === B.y ? ['B'] : []), ...(D.x === D.y ? ['D'] : [])];
    const nota =
      parados.length === 0
        ? ''
        : ` Em ${parados.join(' e ')}, inverter não muda nada: a abscissa é igual à ordenada.`;
    add(COORD_TROCADAS, b, d, [
      txt(
        `Você chegou a ${par(b, d)}: são os vértices certos com as coordenadas invertidas.${nota} `,
      ),
      txt(
        `No par ordenado, a abscissa (x) vem primeiro e a ordenada (y) depois — o certo é ${certo}.`,
      ),
    ]);
  }

  // Juntou as duas abscissas num ponto e as duas ordenadas no outro.
  {
    const b = { x: A.x, y: C.x };
    const d = { x: A.y, y: C.y };
    // Pela mesma razão do erro anterior: com certos números, um dos vértices sai no
    // lugar certo apesar da conta errada, e o texto o citava dos dois lados da frase.
    // O lugar certo pode ser o do OUTRO vértice: B e D trocam de lugar sem deixar de
    // formar ABCD (ver `respostasAceitas`), e no retângulo com C sobre y = x o ponto
    // chamado B cai exatamente em D.
    const ehVerticeCerto = (p: Vec2): boolean => [B, D].some((c) => c.x === p.x && c.y === p.y);
    const acertados = [...(ehVerticeCerto(b) ? ['B'] : []), ...(ehVerticeCerto(d) ? ['D'] : [])];
    const nota =
      acertados.length === 0
        ? ''
        : ` ${acertados.join(' e ')} ${verbo('sair.pret', numeroDe(acertados.length))} num vértice certo por coincidência dos números.`;
    add(COORD_MISTURADAS, b, d, [
      txt(
        `Você chegou a ${par(b, d)}, juntando as duas abscissas dos pontos dados num vértice e as duas ordenadas no outro.${nota} `,
      ),
      txt(
        q.nivel === 'retangulo'
          ? `Cada vértice novo leva a abscissa de um ponto dado e a ordenada do OUTRO: ${certo}.`
          : 'Coordenadas de pontos diferentes não se combinam assim; aqui é preciso o ponto médio e o giro da meia diagonal.',
      ),
    ]);
  }

  // A diagonal tratada como lado: o quadrado construído SOBRE AC.
  {
    const r = { x: -v.y, y: v.x };
    const b = { x: C.x + r.x, y: C.y + r.y };
    const d = { x: A.x + r.x, y: A.y + r.y };
    add(QUAD_DIAGONAL_COMO_LADO, b, d, [
      txt(
        `Com ${par(b, d)}, o segmento AC vira um LADO da figura: de A até C e de C até B, os passos têm o mesmo tamanho. `,
      ),
      txt(`Mas A e C são vértices opostos — AC é a diagonal do ${figura}.`),
    ]);
  }

  // O segmento AC inteiro girado a partir do centro: diagonais perpendiculares, mas de
  // tamanhos diferentes.
  {
    const b = { x: M.x - v.y, y: M.y + v.x };
    const d = { x: M.x + v.y, y: M.y - v.x };
    add(QUAD_LOSANGO, b, d, [
      txt(
        `Você partiu do ponto médio M = ${pontoTxt(M)} e andou o segmento AC INTEIRO, girado: ${passoTxt(-v.y, v.x)}. `,
      ),
      txt(
        `Chegou a ${par(b, d)}. A outra diagonal saiu com o dobro do tamanho de AC, e a figura é um losango. `,
      ),
      txt(
        q.nivel === 'retangulo'
          ? `O ${figura} pedido tem os lados paralelos aos eixos: ${certo}.`
          : `Do centro, anda-se a METADE da diagonal: ${certo}.`,
      ),
    ]);
  }

  if (q.nivel === 'retangulo') {
    // O método do quadrado inclinado aplicado ao retângulo.
    const b = { x: M.x - h.y, y: M.y + h.x };
    const d = { x: M.x + h.y, y: M.y - h.x };
    add(RET_FEZ_QUADRADO, b, d, [
      txt(`Com ${par(b, d)}, os quatro pontos formam um quadrado inclinado com a diagonal AC. `),
      txt(
        `O retângulo pedido tem os lados paralelos aos eixos: cada vértice novo leva a abscissa de um ponto dado e a ordenada do outro — ${certo}.`,
      ),
    ]);
  } else {
    // O método do retângulo aplicado ao quadrado inclinado.
    const b = { x: C.x, y: A.y };
    const d = { x: A.x, y: C.y };
    add(QUAD_RETANGULO, b, d, [
      txt(`Você combinou as coordenadas de A e de C: ${par(b, d)}. `),
      txt(
        'Isso completa um retângulo de lados paralelos aos eixos, que só seria um quadrado se a diagonal AC fizesse 45° com eles — e aqui não faz.',
      ),
    ]);

    // Girou a meia diagonal trocando as coordenadas, sem mudar o sinal.
    const b2 = { x: M.x + h.y, y: M.y + h.x };
    const d2 = { x: M.x - h.y, y: M.y - h.x };
    add(QUAD_GIRO_ERRADO, b2, d2, [
      txt(
        `De M = ${pontoTxt(M)} até C, anda-se ${passoTxt(h.x, h.y)}. Você girou esse caminho só trocando as coordenadas, `,
      ),
      txt(`o que dá ${passoTxt(h.y, h.x)}, e chegou a ${par(b2, d2)}. `),
      txt(
        `No giro de 90°, além de trocar, muda-se o sinal de uma delas: ${passoTxt(-h.y, h.x)}. O certo é ${certo}.`,
      ),
    ]);
  }
  return out;
}

/** A resolução comentada, com os números da variante. */
export function resolucaoDoQuadrilatero(q: Quadrilatero): RichText {
  const { A, C, B, D } = q;
  if (q.nivel === 'retangulo') {
    return [
      txt(
        'Os lados do retângulo são paralelos aos eixos: os lados horizontais mantêm a ordenada, e os verticais, a abscissa.',
      ),
      br,
      txt(
        `Por isso cada vértice novo leva a abscissa de um ponto dado e a ordenada do outro: ` +
          `B = (abscissa de C, ordenada de A) = ${pontoTxt(B)} e D = (abscissa de A, ordenada de C) = ${pontoTxt(D)}.`,
      ),
    ];
  }
  const M = pontoMedio(q);
  const h = { x: C.x - M.x, y: C.y - M.y };
  return [
    txt('As diagonais de um quadrado têm a mesma medida, cortam-se ao meio e são perpendiculares.'),
    br,
    txt(
      `O ponto médio da diagonal AC é M = ${pontoTxt(M)}. De M até C, anda-se ${passoTxt(h.x, h.y)}.`,
    ),
    br,
    txt(
      `A outra diagonal tem o mesmo tamanho e é perpendicular: girando esse caminho 90°, anda-se ` +
        `${passoTxt(-h.y, h.x)} de M para chegar a B, e o caminho contrário, ${passoTxt(h.y, -h.x)}, para chegar a D.`,
    ),
    br,
    txt(`Assim, B = ${pontoTxt(B)} e D = ${pontoTxt(D)}.`),
    br,
    txt(
      `Combinar as coordenadas de A e C — ${pontoTxt({ x: C.x, y: A.y })} e ${pontoTxt({ x: A.x, y: C.y })} — ` +
        'daria um retângulo, que só seria quadrado se a diagonal fizesse 45° com os eixos.',
    ),
  ];
}

export interface ContextoQuadrilatero {
  readonly abertura: (nivel: NivelQuadrilatero) => string;
}

/** Contextos com a forma no lugar certo da frase: "canteiro quadrado" / "retangular". */
export const CONTEXTOS_QUADRILATERO: readonly ContextoQuadrilatero[] = [
  {
    abertura: (n) =>
      `Um jardineiro vai cercar um canteiro ${n === 'retangulo' ? 'retangular' : 'quadrado'}. No mapa do terreno, representado no plano cartesiano abaixo, ele já fincou estacas em dois cantos do canteiro.`,
  },
  {
    abertura: (n) =>
      `Uma artista está desenhando ${n === 'retangulo' ? 'um retângulo' : 'um quadrado'} num programa de computador. Na tela, representada no plano cartesiano abaixo, ela já posicionou dois vértices da figura.`,
  },
  {
    abertura: (n) =>
      `A planta de um bairro, representada no plano cartesiano abaixo, mostra dois cantos de uma praça ${n === 'retangulo' ? 'retangular' : 'quadrada'} que será construída.`,
  },
];

// ---------------------------------------------------------------------------
// Figuras: o plano cartesiano e a ficha
// ---------------------------------------------------------------------------

/** Unidades de desenho por unidade do plano. */
export const U_PLANO = 40;
/** O plano vai de −N_PLANO a N_PLANO nos dois eixos. */
export const N_PLANO = 6;

export const quadroDoPlano = (): Rect => {
  const L = (N_PLANO + 0.95) * U_PLANO;
  // Em cima, folga a mais: o "y" do eixo fica acima da seta, e a prancheta (que corta
  // o que sai do quadro) o decepava.
  return { x: -L, y: -L, w: 2 * L, h: 2 * L + 0.5 * U_PLANO };
};

/** Malha, eixos numerados na origem e nos extremos, e os dois vértices dados. */
export function cenarioDoPlano(q: Quadrilatero): Shape[] {
  const tela = (v: Vec2): Vec2 => ({ x: v.x * U_PLANO, y: v.y * U_PLANO });
  return [
    gradeQuadriculada(-N_PLANO, N_PLANO, -N_PLANO, N_PLANO, U_PLANO),
    ...eixosNumerados(N_PLANO, U_PLANO),
    { t: 'point', p: tela(q.A), role: 'given', name: 'A' },
    { t: 'point', p: tela(q.C), role: 'given', name: 'C' },
  ];
}

export function descricaoDoPlano(q: Quadrilatero): string {
  const forma =
    q.nivel === 'retangulo'
      ? 'vértices opostos de um retângulo de lados paralelos aos eixos'
      : 'vértices opostos de um quadrado';
  return (
    `Plano cartesiano com malha quadriculada de −${N_PLANO} a ${N_PLANO} nos dois eixos, de 1 em 1; os eixos trazem escritos só o 0 e os extremos, −${N_PLANO} e ${N_PLANO}. ` +
    `Estão marcados o ponto A, em ${pontoTxt(q.A)}, e o ponto C, em ${pontoTxt(q.C)}, ${forma}.`
  );
}

export const figuraDoPlano = (q: Quadrilatero): Scene =>
  scene(cenarioDoPlano(q), {
    a11y: { title: 'Plano cartesiano com dois vértices opostos', desc: descricaoDoPlano(q) },
    viewBox: quadroDoPlano(),
  });

type Lado = CoordinateEntryInteraction['pontos'][number]['lado'];

/**
 * A FICHA: o quadrilátero desenhado à parte, fora de escala mas com a mesma forma e a
 * mesma inclinação do que está no plano, como no material do GeoGebra. A e C levam as
 * coordenadas escritas; B e D, os campos. Cada rótulo cresce para fora da figura.
 */
export function fichaDoQuadrilatero(q: Quadrilatero): {
  readonly cena: Scene;
  readonly pontos: CoordinateEntryInteraction['pontos'];
} {
  const M = pontoMedio(q);
  const vs = [q.A, q.B, q.C, q.D];
  const rel = vs.map((p) => ({ x: p.x - M.x, y: p.y - M.y }));
  const ext = Math.max(...rel.map((p) => Math.max(Math.abs(p.x), Math.abs(p.y))));
  const s = 52 / ext;
  const pts = rel.map((p) => ({ x: p.x * s, y: p.y * s }));
  const lado = (p: Vec2): Lado => {
    const ax = Math.abs(p.x);
    const ay = Math.abs(p.y);
    if (q.nivel === 'inclinado' && ax > 1.8 * ay) return p.x > 0 ? 'e' : 'w';
    if (q.nivel === 'inclinado' && ay > 1.8 * ax) return p.y > 0 ? 'n' : 's';
    return `${p.y > 0 ? 'n' : 's'}${p.x > 0 ? 'e' : 'w'}` as Lado;
  };
  const nomes = ['A', 'B', 'C', 'D'] as const;
  const figura = q.nivel === 'retangulo' ? 'retângulo' : 'quadrado';
  const cena = scene(
    [
      { t: 'poly', pts, closed: true, role: 'given', fill: 'given-fill' },
      ...pts.map((p): Shape => ({ t: 'point', p, role: 'given' })),
    ],
    {
      a11y: {
        title: `Ficha do ${figura} ABCD`,
        desc:
          `Desenho do ${figura} ABCD fora de escala. Junto de A está escrito ${pontoTxt(q.A)}, e junto de C, ` +
          `${pontoTxt(q.C)}. Junto de B e de D há espaços para escrever as coordenadas.`,
      },
      // Margem larga dos lados: é onde crescem os rótulos com os campos.
      viewBox: { x: -215, y: -100, w: 430, h: 200 },
    },
  );
  const pontos = pts.map((p, i) => ({
    nome: nomes[i] as string,
    em: p,
    lado: lado(p),
    ...(i % 2 === 0 ? { valor: vs[i] as Vec2 } : {}),
  }));
  return { cena, pontos };
}

/** A frase que diz o que se sabe da figura — parte do texto-base, antes do suporte. */
export function relacaoDosDados(nivel: NivelQuadrilatero): {
  antes: string;
  destaque: string;
  depois: string;
} {
  return nivel === 'retangulo'
    ? {
        antes: 'Os pontos A e C são vértices ',
        destaque: 'opostos',
        depois: ' do retângulo ABCD, cujos lados são paralelos aos eixos coordenados.',
      }
    : {
        antes: 'Os pontos A e C são vértices ',
        destaque: 'opostos',
        // Vértices opostos já dizem que AC é diagonal.
        depois: ' do quadrado ABCD.',
      };
}
